import React, { useContext, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import API from '../../../services/API'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AnDateTimePicker from '../../../components/DateTime/DateTimePicker'
import VDICheckForm from '../../governance/cleaningSchedules/VehicleCleanForm/VDICheckForm'
import SmallModal from '../../../components/Modals/SmallModal'
import Lottie from 'lottie-react'
import successLottie from '../../../assets/lottie/formSubmission2.json'
import { useNavigate } from 'react-router-dom'
import DrugBags from '../VDIForm/DrugsBags'
import Attachments from '../../../components/Attachments/Attachments'
import { GlobalContext } from '../../../App'
import Loading from '../../loading/loadingPage'
import SubscriptionGate from '../../../components/Subscriptions/SubscriptionGate'
import { MakeReadyContext } from '../makeReady/MakeReady'
import MakeReadyCheckPanel from '../makeReady/MakeReadyCheckPanel'
import UnsavedChanges from '../../../components/UnsavedChanges/UnsavedChanges'

const StartOfShiftCheck = () => {
  const [vehicleOptions, setVehicleOptions] = useState([])
  const [selectedVehicle, setSelectedVehicle] = useState(null)

  // Vehicle check (VDI)
  const [vdiData, setVdiData] = useState({})
  const [loadingVDIOptions, setLoadingVDIOptions] = useState(false)
  const [vdiTime, setVDITime] = useState(moment())
  const [fuelAmount, setFuelAmount] = useState('')
  const [mileage, setMileage] = useState('')
  const [notes, setNotes] = useState('')
  const [defectReportRequired, setDefectReportRequired] = useState(false)
  const [vehicleUseable, setVehicleUseable] = useState(true)
  const [attachments, setAttachments] = useState([])
  const [vehicleOOSError, setVehicleOOSError] = useState(false)

  // Equipment & Consumables (Make Ready)
  const [equipmentLoading, setEquipmentLoading] = useState(false)
  const [loadList, setLoadList] = useState({})
  const [inventory, setInventory] = useState({ items: [] })
  const [assetVehicle, setAssetVehicle] = useState({})
  const [equipmentDirty, setEquipmentDirty] = useState(false)
  const equipmentPanelRef = useRef()

  // Drug bags
  const [drugBags, setDrugBags] = useState([])

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [equipmentRetryNeeded, setEquipmentRetryNeeded] = useState(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [issueWarning, setIssueWarning] = useState(null)

  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)

  useEffect(() => {
    API.get('/vehicleList')
      .then(
        (res) =>
          res.status === 200 &&
          setVehicleOptions(res.data.filter((x) => x.vehicleType)),
      )
      .catch((e) => console.log(e))
  }, [])

  const hasEquipmentChecklist = !!selectedVehicle?.makeReadyLoadList

  useEffect(() => {
    if (!selectedVehicle) return

    setLoadingVDIOptions(true)
    API.post('/getvdioptions', { vehicleType: selectedVehicle.vehicleType })
      .then((res) => res.status === 200 && setVdiData(res.data))
      .finally(() => setLoadingVDIOptions(false))

    if (selectedVehicle.makeReadyLoadList) {
      setEquipmentLoading(true)
      API.post('/getmakereadydata', {
        callsignAssetID: selectedVehicle._id,
      })
        .then((res) => {
          if (res.status === 200) {
            setLoadList(res.data.loadList)
            setInventory(res.data.inventory)
            setAssetVehicle(res.data.assetVehicle)
          }
        })
        .finally(() => setEquipmentLoading(false))
    } else {
      setLoadList({})
      setInventory({ items: [] })
      setAssetVehicle({})
    }
  }, [selectedVehicle])

  const somethingNotComplete = Object.keys(vdiData).some((x) =>
    vdiData[x].some((y) => y.value !== false && y.value !== true),
  )

  const somethingWrong = Object.keys(vdiData).some((x) =>
    vdiData[x].some((y) => y.value === false),
  )

  const formDirty =
    !!selectedVehicle &&
    (Object.keys(vdiData).length > 0 || equipmentDirty || drugBags.length > 0)

  const submitEquipmentCheck = async () => {
    if (!equipmentPanelRef.current.isValid()) {
      throw new Error(
        'Confirm every expected asset. Items marked Missing or Removed can be submitted, but Replaced or Assigned items need the replacement asset selected.',
      )
    }
    const payload = equipmentPanelRef.current.getPayload()
    const res = await API.post('/updatemakereadyinventory', payload)
    if (res.status !== 200) throw new Error('Equipment check failed to save')
    equipmentPanelRef.current.clearDirty()
  }

  // Bags selected in the Drugs Bags section that live elsewhere get
  // reassigned to this vehicle - /medsmx/newlocation writes the medicine
  // log entry ("Bag moved to <callSign>") server-side.
  const assignDrugBagsToVehicle = async () => {
    const bagsToAssign = drugBags.filter((entry) => entry.assignToVehicle)
    const failed = []
    for (const entry of bagsToAssign) {
      try {
        const res = await API.post('/medsmx/newlocation', {
          id: entry.bag,
          vehicle: selectedVehicle._id,
        })
        if (res.status !== 200) failed.push(entry)
      } catch (assignError) {
        console.error(assignError)
        failed.push(entry)
      }
    }
    if (failed.length > 0) {
      setToast({
        message: `The check saved, but ${failed.length} drug bag${
          failed.length === 1 ? '' : 's'
        } could not be assigned to this vehicle. Assign ${
          failed.length === 1 ? 'it' : 'them'
        } from the Check Medicine Bag page.`,
        severity: 'warning',
      })
    }
  }

  // Bags reported untagged (when the record says they have a seal) get
  // their seal marked removed with a medicine log entry, so the governance
  // opened-bags count sees them.
  const reportRemovedSeals = async () => {
    const toReport = drugBags.filter((entry) => entry.reportSealRemoved)
    const failed = []
    for (const entry of toReport) {
      try {
        const res = await API.post('/medsmx/report-seal-removed', {
          id: entry.bag,
        })
        if (res.status !== 200) failed.push(entry)
      } catch (reportError) {
        console.error(reportError)
        failed.push(entry)
      }
    }
    if (failed.length > 0) {
      setToast({
        message: `The check saved, but ${failed.length} bag seal${
          failed.length === 1 ? '' : 's'
        } could not be reported as removed. Record ${
          failed.length === 1 ? 'it' : 'them'
        } from the Check Medicine Bag page.`,
        severity: 'warning',
      })
    }
  }

  const submitStartOfShift = async ({ skipIssueWarning = false } = {}) => {
    setSubmitError('')
    setVehicleOOSError(false)

    if (hasEquipmentChecklist && !equipmentPanelRef.current.isValid()) {
      setSubmitError(
        'Confirm every expected asset in the equipment checklist. Missing or removed items can still be submitted and will be flagged for follow-up, but replaced or assigned items need the asset selected.',
      )
      return
    }

    const issueSummary = hasEquipmentChecklist
      ? equipmentPanelRef.current.getIssueSummary?.()
      : null

    if (
      !skipIssueWarning &&
      issueSummary &&
      Number(issueSummary.totalIssues || 0) > 0
    ) {
      setIssueWarning(issueSummary)
      return
    }

    setIsSubmitting(true)

    try {
      const vdiRes = await API.post('/vehicles/vdi', {
        checks: vdiData,
        vehicle: selectedVehicle._id,
        date: vdiTime,
        fuelAmount,
        mileage,
        notes,
        vehicleUseable,
        defectReportRequired,
        drugBags,
        attachments,
      })

      if (vdiRes.status !== 200) {
        setSubmitError('Failed to save the vehicle check. Please try again.')
        return
      }

      await assignDrugBagsToVehicle()
      await reportRemovedSeals()

      if (hasEquipmentChecklist) {
        try {
          await submitEquipmentCheck()
        } catch (equipmentError) {
          console.error(equipmentError)
          setEquipmentRetryNeeded(true)
          return
        }
      }

      setShowSuccessMessage(true)
    } catch (error) {
      console.error(error)

      if (
        error.response?.status === 400 &&
        error.response?.data === 'Vehicle Out of Service'
      ) {
        setVehicleOOSError(true)
        return
      }

      setSubmitError(
        'Failed to save the vehicle check. Please check your connection and try again.',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmit = async (e) => {
    e?.preventDefault?.()
    await submitStartOfShift()
  }

  const handleRetryEquipment = async () => {
    setIsSubmitting(true)
    try {
      await submitEquipmentCheck()
      setEquipmentRetryNeeded(false)
      setShowSuccessMessage(true)
    } catch (equipmentError) {
      console.error(equipmentError)
    } finally {
      setIsSubmitting(false)
    }
  }

  const sortedVehicleOptions = [...vehicleOptions].sort((a, b) => {
    const typeCompare = a.vehicleType.localeCompare(b.vehicleType)
    if (typeCompare !== 0) return typeCompare
    return a.callSign.localeCompare(b.callSign)
  })

  return (
    <Stack
      gap={1}
      component="form"
      onSubmit={equipmentRetryNeeded ? (e) => e.preventDefault() : handleSubmit}
    >
      <Stack gap={1} component={Paper} padding={1}>
        <Typography variant="title2">Start of Shift Check</Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} lg={3}>
            <Autocomplete
              options={sortedVehicleOptions}
              disableClearable
              getOptionLabel={(opt) =>
                opt?.status === 'Out of Service'
                  ? `${opt.callSign} - ${opt.status}`
                  : (opt?.callSign ?? '')
              }
              renderOption={(props, option) => {
                const { key, ...optionProps } = props
                return (
                  <li key={key} {...optionProps}>
                    <Typography
                      sx={{
                        color:
                          option.status === 'Out of Service'
                            ? 'anRed.main'
                            : 'anBlue.main',
                        fontWeight:
                          option.status === 'Out of Service'
                            ? 'inherit'
                            : 'bold',
                      }}
                    >
                      {option.callSign}
                      {option.status === 'Out of Service' &&
                        ` - ${option.status}`}
                    </Typography>
                  </li>
                )
              }}
              onChange={(e, value) => {
                setSelectedVehicle(value)
                vehicleOOSError && setVehicleOOSError(false)
              }}
              value={selectedVehicle}
              isOptionEqualToValue={(option, value) =>
                option?._id === value?._id
              }
              groupBy={(opt) => opt.vehicleType}
              getOptionDisabled={(opt) => opt.status === 'Out of Service'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vehicle"
                  required
                  autoComplete="off"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <AnDateTimePicker
              fullWidth
              date={vdiTime}
              onChange={(value) => setVDITime(value)}
              label="Check Date/Time"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth>
              <InputLabel id="fuelAmount-label">Fuel Amount</InputLabel>
              <Select
                labelId="fuelAmount-label"
                label="Fuel Amount"
                value={fuelAmount}
                onChange={(e) => setFuelAmount(e.target.value)}
                required
              >
                <MenuItem value="Empty">Empty</MenuItem>
                <MenuItem value="1/4">1/4</MenuItem>
                <MenuItem value="1/2">1/2</MenuItem>
                <MenuItem value="3/4">3/4</MenuItem>
                <MenuItem value="Full">Full</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              value={mileage}
              onChange={(e) => setMileage(e.target.value)}
              type="number"
              required
              fullWidth
              label="Mileage"
              autoComplete="off"
            />
          </Grid>
        </Grid>
        {vehicleOOSError && (
          <Alert severity="error">
            This vehicle is Out of Service and cannot have a check recorded
            against it.
          </Alert>
        )}
      </Stack>

      {loadingVDIOptions && <Loading hideText />}

      {selectedVehicle && !loadingVDIOptions && (
        <>
          <VDICheckForm
            data={vdiData}
            setData={setVdiData}
            selectedVehicleType={selectedVehicle.vehicleType}
          />

          <Stack component={Paper} padding={1} gap={1}>
            <Typography variant="title2">Equipment & Consumables</Typography>
            {hasEquipmentChecklist && !equipmentLoading && (
              <Alert severity="info" variant="outlined">
                If equipment or consumables are missing, mark them as missing
                and continue. The check will still complete, and the shortfall
                will be flagged for make-ready trends and Fleet KPIs.
              </Alert>
            )}
            {equipmentLoading && <Loading hideText />}
            {!equipmentLoading && !hasEquipmentChecklist && (
              <Alert severity="info">
                No equipment checklist is configured for this vehicle.
              </Alert>
            )}
            {!equipmentLoading && hasEquipmentChecklist && (
              <SubscriptionGate requiredTier={2} featureName="Make Ready" inline>
                <MakeReadyContext.Provider
                  value={{
                    loadList,
                    inventory,
                    setInventory,
                    callsignAssetID: selectedVehicle._id,
                    assetVehicle,
                  }}
                >
                  <MakeReadyCheckPanel
                    ref={equipmentPanelRef}
                    showPickListButton={false}
                    onDirtyChange={setEquipmentDirty}
                  />
                </MakeReadyContext.Provider>
              </SubscriptionGate>
            )}
          </Stack>

          <DrugBags
            drugBags={drugBags}
            setDrugBags={setDrugBags}
            vehicleId={selectedVehicle._id}
          />

          <Stack component={Paper} padding={1} gap={1}>
            <TextField
              label="Notes"
              value={notes}
              multiline
              minRows={2}
              onChange={(e) => setNotes(e.target.value)}
              required={somethingWrong}
              helperText={
                somethingWrong && 'Required when something is marked as a fault'
              }
            />
            {somethingWrong && (
              <>
                <FormControl>
                  <InputLabel>
                    Does this check contain defects that need reporting?
                  </InputLabel>
                  <Select
                    label="Does this check contain defects that need reporting?"
                    value={defectReportRequired}
                    onChange={(e) => setDefectReportRequired(e.target.value)}
                  >
                    <MenuItem value={false}>No</MenuItem>
                    <MenuItem value={true}>Yes</MenuItem>
                  </Select>
                </FormControl>
                <FormControl>
                  <InputLabel>
                    Is the vehicle safe to continue using?
                  </InputLabel>
                  <Select
                    label="Is the vehicle safe to continue using?"
                    value={vehicleUseable}
                    onChange={(e) => setVehicleUseable(e.target.value)}
                  >
                    <MenuItem value={true}>Yes</MenuItem>
                    <MenuItem value={false}>No</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </Stack>

          <Stack component={Paper} padding={1} gap={1}>
            <Attachments
              s3folder="fleetequipment/vdi-attachments"
              actionAfterUpload={(s3key) =>
                setAttachments((prev) => [...prev, s3key])
              }
              actionAfterDelete={() => {
                // Nothing to do yet
              }}
            />
          </Stack>

          <Stack gap={1} component={Paper} padding={2}>
            {somethingNotComplete && (
              <Alert severity="info">
                All vehicle check items need to be marked as complete or
                incomplete
              </Alert>
            )}
            {submitError && <Alert severity="error">{submitError}</Alert>}
            {equipmentRetryNeeded && (
              <Alert
                severity="warning"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleRetryEquipment}
                    disabled={isSubmitting}
                  >
                    Retry
                  </Button>
                }
              >
                The vehicle check saved, but the equipment check failed to
                save. Retry just the equipment check below.
              </Alert>
            )}
            {!equipmentRetryNeeded && (
              <Button
                fullWidth
                color="success"
                variant="contained"
                type="submit"
                disabled={
                  somethingNotComplete ||
                  Object.keys(vdiData).length === 0 ||
                  isSubmitting
                }
              >
                {isSubmitting ? 'Submitting...' : 'Complete Start of Shift Check'}
              </Button>
            )}
            {isSubmitting && <LinearProgress />}
          </Stack>
        </>
      )}

      <UnsavedChanges
        changesPresent={formDirty && !showSuccessMessage}
        handleSave={handleSubmit}
        title="START OF SHIFT CHECK IN PROGRESS"
        saveButtonText="Complete Check"
      />

      <Dialog
        open={!!issueWarning}
        onClose={isSubmitting ? null : () => setIssueWarning(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Submit with missing items?
        </DialogTitle>
        <DialogContent>
          <Stack gap={1}>
            <Typography variant="body2">
              This start of shift check has equipment issues recorded. You can
              still submit it, but the shortfalls will be flagged for restock,
              make-ready trends and Fleet KPIs.
            </Typography>
            {issueWarning?.missingItems > 0 && (
              <Typography variant="body2" fontWeight={700}>
                Missing consumables: {issueWarning.missingItems}
              </Typography>
            )}
            {issueWarning?.expiredItems > 0 && (
              <Typography variant="body2" fontWeight={700}>
                Expired items: {issueWarning.expiredItems}
              </Typography>
            )}
            {issueWarning?.assetIssues > 0 && (
              <Typography variant="body2" fontWeight={700}>
                Asset issues: {issueWarning.assetIssues}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            color="inherit"
            onClick={() => setIssueWarning(null)}
            disabled={isSubmitting}
          >
            Review Check
          </Button>
          <Button
            type="button"
            variant="contained"
            color="warning"
            onClick={() => {
              setIssueWarning(null)
              submitStartOfShift({ skipIssueWarning: true })
            }}
            disabled={isSubmitting}
          >
            Submit Anyway
          </Button>
        </DialogActions>
      </Dialog>

      <SmallModal open={showSuccessMessage}>
        <Stack>
          <Typography variant="title2" fontSize="1em" textAlign="center">
            Start of Shift Check Recorded
          </Typography>
          <Lottie animationData={successLottie} loop={false} />
          <Button
            onClick={() => navigate('/')}
            variant="contained"
            size="small"
            color="anBlue"
          >
            Return Home
          </Button>
        </Stack>
      </SmallModal>
    </Stack>
  )
}

export default StartOfShiftCheck
